import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Snackbar,
  Alert,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import AppLogo from "../components/app/AppLogo";
import {
  isPasswordStrong,
  getPasswordStrength,
  PASSWORD_RULE_KEYS,
} from "../utils/passwordRules";

function ResetPassword() {
  const { uid, token } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordStrength, setPasswordStrength] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const baseURL = process.env.REACT_APP_API_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setSnackbar({
        open: true,
        message: t("Passwords do not match"),
        severity: "error",
      });
      return;
    }

    if (!isPasswordStrong(password)) {
      setSnackbar({
        open: true,
        message: t("PasswordDoesNotMeetRules"),
        severity: "error",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(
        baseURL + "/api/password-reset-confirm/",
        {
          uid,
          token,
          new_password: password,
        }
      );

      if (response.status === 200) {
        setSnackbar({
          open: true,
          message: t("Password reset successful! Please login."),
          severity: "success",
        });
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      } else {
        setSnackbar({
          open: true,
          message: t("An error occurred. Please try again."),
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error.response?.data?.detail || t("Invalid link or expired token."),
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      sx={{ backgroundColor: "#f5f5f5" }}
    >
      <Paper elevation={5} sx={{ padding: 4, width: 350, textAlign: "center" }}>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
          <AppLogo height={90} />
        </Box>
        <Typography variant="h5" mb={2}>
          {t("Reset Your Password")}
        </Typography>

        <form onSubmit={handleSubmit}>
          <TextField
            label={t("New Password")}
            variant="outlined"
            type="password"
            fullWidth
            margin="normal"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setPasswordStrength(getPasswordStrength(e.target.value));
            }}
            required
          />

          <Typography
            variant="caption"
            color={
              passwordStrength === "Weak"
                ? "error"
                : passwordStrength === "Medium"
                ? "warning.main"
                : "success.main"
            }
          >
            {t("Password Strength")}: {t(passwordStrength)}
          </Typography>

          <Box component="ul" sx={{ textAlign: "left", pl: 2, mt: 1 }}>
            {PASSWORD_RULE_KEYS.map((key) => (
              <Typography
                component="li"
                variant="caption"
                key={key}
                display="block"
              >
                {t(key)}
              </Typography>
            ))}
          </Box>

          <TextField
            label={t("Confirm Password")}
            variant="outlined"
            type="password"
            fullWidth
            margin="normal"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />

          <Button
            type="submit"
            variant="contained"
            color="warning"
            fullWidth
            sx={{ marginTop: 2 }}
            disabled={isSubmitting || !isPasswordStrong(password)}
          >
            {isSubmitting ? t("Please wait...") : t("Reset Password")}
          </Button>
        </form>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ResetPassword;
